<template>
  <div class="fixed inset-0 z-100 flex flex-col bg-[#f9f9fa] tablet-v:bg-white">
    <div
      class="relative hidden h-[54px] shrink-0 items-center justify-between border-b border-[#e5e5e5] bg-white pl-[21px] pr-4 tablet-v:flex"
    >
      <div
        class="pointer-events-none absolute inset-0 flex items-center justify-center"
      >
        <DacrIcon class="h-[30px] w-auto" />
      </div>
      <div class="relative flex items-center gap-2.5">
        <Button
          v-if="showDownloadButton"
          :icon="DownloadIcon"
          variant="stroke"
          class="w-8 px-0!"
          aria-label="Download"
          @click="handleDownloadClick"
        />
        <Button :icon="ShareIcon" variant="stroke" @click="handleShareClick">
          Share
        </Button>
      </div>
      <div class="relative flex items-center gap-2.5">
        <Button
          v-if="isOwner"
          :icon="GearIcon"
          variant="stroke"
          @click="handleFileSettingsClick"
        >
          Copyright settings
        </Button>
        <Button
          :icon="CloseIcon"
          variant="ghost"
          class="w-[30px] px-0!"
          aria-label="Close preview"
          @click="handleClose"
        />
      </div>
    </div>

    <div class="relative min-h-0 flex-1">
      <div
        ref="headerEl"
        class="absolute inset-x-0 top-0 z-10 h-[calc(env(safe-area-inset-top)+60px)] pt-[calc(env(safe-area-inset-top)+6px)] tablet-v:hidden"
      >
        <div
          class="absolute inset-0 bg-[#fbfbfb]/70 backdrop-blur-xl transition-opacity duration-250"
          :class="glassHeader ? 'opacity-100' : 'opacity-0'"
        />
        <div
          class="pointer-events-none absolute inset-x-0 bottom-2.5 top-[calc(env(safe-area-inset-top)+6px)] flex items-center justify-center"
        >
          <DacrIcon class="h-[30px] w-auto" />
        </div>
        <button
          type="button"
          aria-label="Close preview"
          class="relative ml-4 flex size-11 cursor-pointer items-center justify-center rounded-full bg-[#fbfbfb]/70 backdrop-blur-xl"
          @click="handleClose"
        >
          <span
            class="relative flex size-full items-center justify-center rounded-full"
          >
            <span aria-hidden="true" class="shadow--glass-pill"><i></i></span>
            <CloseIcon class="size-6 text-gray-800" aria-hidden="true" />
          </span>
        </button>
      </div>

      <div
        ref="zoomViewportEl"
        class="absolute inset-0 overflow-hidden bg-[#f9f9fa]"
        :style="zoom.viewportStyle.value"
      >
        <div
          ref="paneEl"
          class="absolute inset-0 origin-center overflow-y-auto overscroll-contain"
          :style="zoom.contentStyle.value"
          @scroll="updateGlassHeader"
        >
          <div
            v-if="isPagesPreview"
            class="flex min-h-full flex-col items-center gap-10 px-5 pb-5 pt-[calc(env(safe-area-inset-top)+80px)] tablet-v:p-[30px]"
            :class="totalPages === 1 && 'justify-center'"
          >
            <div
              v-for="index in totalPages"
              :key="index"
              :ref="index === 1 ? setFirstPageEl : undefined"
              class="shrink-0 bg-white"
              :style="pageStyle(index)"
            >
              <img
                :src="getPageUrl(index)"
                loading="lazy"
                alt=""
                class="pointer-events-none size-full select-none"
                @load="handlePageLoad(index, $event)"
              />
            </div>
          </div>
          <div
            v-else
            class="size-full pt-[calc(env(safe-area-inset-top)+60px)] tablet-v:p-10"
          >
            <img
              v-if="previewImageUrl && !imageErrored"
              :src="previewImageUrl"
              :alt="props.file.name"
              ref="imageEl"
              class="size-full select-none object-contain"
              draggable="false"
              @load="updateGlassHeader"
              @error="imageErrored = true"
            />
            <div v-else class="flex size-full items-center justify-center">
              <div class="size-[170px] tablet-v:size-[280px]">
                <Thumbnail :type="thumbnailType" :alt="props.file.name" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="flex shrink-0 items-center justify-between border-t border-[#e5e5e5] bg-white px-4 pt-2 pb-[calc(env(safe-area-inset-bottom)+20px)] tablet-v:hidden"
    >
      <div class="flex items-center gap-2.5">
        <Button
          v-if="showDownloadButton"
          variant="stroke"
          size="lg"
          :icon="DownloadIcon"
          class="w-[38px] px-0!"
          aria-label="Download"
          @click="handleDownloadClick"
        />
        <Button
          :icon="ShareIcon"
          variant="stroke"
          size="lg"
          @click="handleShareClick"
        >
          Share
        </Button>
      </div>
      <Button
        v-if="isOwner"
        :icon="GearIcon"
        variant="stroke"
        size="lg"
        aria-label="Copyright settings"
        @click="handleFileSettingsClick"
      >
        <template v-if="isViewportWideEnough">Copyright settings</template>
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { FilePermission, FileRole } from '@/shared/graphql/router/graphql'
import type { CopyrightFile } from '@/features/copyright/composables/useCopyrightFile'
import { useDevice } from '@/shared/composables/useDevice'
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { openModal, Modals } from '@/shared/dialogs/modals'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import { Button, Thumbnail } from '@dacr/library'
import {
  CloseIcon,
  DacrIcon,
  DownloadIcon,
  GearIcon,
  ShareIcon,
} from '@dacr/icons'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'
import padStart from 'lodash.padstart'
import { usePreviewZoom } from '@/features/explorer/composables/usePreviewZoom'

const props = defineProps<{
  file: CopyrightFile
}>()
const emit = defineEmits<{
  closeViewer: []
}>()

const MAX_PAGE_WIDTH = 800
const DEFAULT_PAGE_RATIO = 1 / Math.SQRT2

const device = useDevice()

const headerEl = ref<HTMLElement>()
const paneEl = ref<HTMLElement>()
const zoomViewportEl = ref<HTMLElement>()
const imageEl = ref<HTMLImageElement>()
const firstPageEl = ref<HTMLElement>()
const pane = reactive({ width: 0, height: 0, top: 0, padding: 0 })
const pageRatios = reactive<Record<number, number>>({})
const glassHeader = ref(false)
const imageErrored = ref(false)
let resizeObserver: ResizeObserver | null = null

const setFirstPageEl = (el: unknown) => {
  firstPageEl.value = el instanceof HTMLElement ? el : undefined
}

const measurePane = () => {
  const el = paneEl.value?.firstElementChild
  if (!(el instanceof HTMLElement)) return
  const style = getComputedStyle(el)
  pane.width = el.clientWidth
  pane.height = paneEl.value?.clientHeight ?? 0
  pane.top = parseFloat(style.paddingTop)
  pane.padding = parseFloat(style.paddingBottom)
  zoom.reset()
  updateGlassHeader()
}

const updateGlassHeader = () => {
  const header = headerEl.value
  const content = firstPageEl.value ?? imageEl.value
  if (!header || !content || !header.offsetHeight) {
    glassHeader.value = false
    return
  }
  const rect = content.getBoundingClientRect()
  let top = rect.top
  const image = imageEl.value
  if (content === image && image.naturalWidth && image.naturalHeight) {
    const fit = Math.min(
      rect.width / image.naturalWidth,
      rect.height / image.naturalHeight
    )
    top += (rect.height - image.naturalHeight * fit) / 2
  }
  glassHeader.value = top < header.getBoundingClientRect().bottom
}

const zoom = usePreviewZoom(zoomViewportEl, paneEl, updateGlassHeader)
watch(
  () => props.file.id,
  () => {
    zoom.reset()
    imageErrored.value = false
    if (paneEl.value) paneEl.value.scrollTop = 0
  }
)

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
  if (!paneEl.value) return
  resizeObserver = new ResizeObserver(measurePane)
  resizeObserver.observe(paneEl.value)
  measurePane()
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown)
  resizeObserver?.disconnect()
  resizeObserver = null
})

const previewImageUrl = computed(() =>
  props.file.asset?.previewImageUrl
    ? applyAuthorizationToken(props.file.asset.previewImageUrl)
    : undefined
)

const thumbnailType = computed(() => fileTypeToThumbnail(props.file.type))

const isPagesPreview = computed(
  () => props.file.asset?.__typename === 'DocumentAsset'
)

const totalPages = computed(() =>
  props.file.asset?.__typename === 'DocumentAsset'
    ? props.file.asset.totalPages
    : undefined
)

const documentUrl = computed(() =>
  props.file.asset?.__typename === 'DocumentAsset'
    ? props.file.asset.documentUrl
    : undefined
)

const pageStyle = (index: number) => {
  const ratio = pageRatios[index] ?? DEFAULT_PAGE_RATIO
  const availableWidth = Math.max(0, pane.width - pane.padding * 2)
  const availableHeight = pane.height - pane.top - pane.padding
  const width = Math.min(
    MAX_PAGE_WIDTH,
    availableWidth,
    availableHeight > 0 ? availableHeight * ratio : MAX_PAGE_WIDTH
  )
  return { width: `${width}px`, aspectRatio: String(ratio) }
}

const handlePageLoad = (index: number, event: Event) => {
  const img = event.target as HTMLImageElement
  if (img.naturalWidth > 0 && img.naturalHeight > 0) {
    pageRatios[index] = img.naturalWidth / img.naturalHeight
    requestAnimationFrame(updateGlassHeader)
  }
}

const isOwner = computed(() => props.file.role === FileRole.Owner)

const isViewportWideEnough = computed(() => device.viewportWidth.value >= 375)

const showDownloadButton = computed(() =>
  props.file.permissions.includes(FilePermission.Download)
)

const handleClose = () => {
  emit('closeViewer')
}

const handleKeydown = (event: KeyboardEvent) => {
  if (event.code === 'Escape') {
    handleClose()
  }
}

const getPageUrl = (index: number) => {
  const url = documentUrl.value
  if (!url) return ''

  const padding = totalPages.value?.toString().length
  const pageString = padStart(index.toString(), padding, '0')

  return (
    applyAuthorizationToken(url.replace(/-(.*)\./, `-${pageString}.`)) ?? ''
  )
}

const handleFileSettingsClick = () => {
  openDialog(Dialog.FileSettings, {
    props: { file: props.file },
  })
}

const handleShareClick = () => {
  openDialog(Dialog.Share, {
    props: { file: props.file },
  })
}

const handleDownloadClick = () => {
  openModal(Modals.Download, {
    props: { file: props.file },
  })
}
</script>
